"use client";

// ============================================================
// CLUBHUB – Funktion setzen und Zugehoerigkeit beenden
// ============================================================
// Beides aendert die Zeit, nicht nur den Wert. Eine neue Funktion
// nach einer Wahl BEENDET die alte zum Stichtag und beginnt eine
// neue — so steht in der Historie, wer von wann bis wann Kassenwart
// war. Nur ein Tippfehler wird an Ort und Stelle korrigiert. Wer die
// Gruppe verlaesst, bekommt ein „bis“ — geloescht wird nichts.
// ============================================================
import { useState } from "react";
import { History } from "lucide-react";
import {
  Dialog,
  DialogCancel,
  DialogChoice,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogNotice,
  DialogSubmit,
  DialogTitle,
  Input,
  toast,
} from "@/components/ui";
import { istFehler, type ActionResult } from "@/lib/action-result";
import type { GruppeView, ZugehoerigkeitView } from "@/lib/gruppen/views";
import { istIsoDatum, plusTage } from "@/lib/gruppen/datum";
import { formatDate } from "@/lib/utils";
import { useGruppenBestand } from "./gruppen-bestand";
import { FunktionsFeld } from "./funktions-feld";

type Modus = "wechsel" | "korrektur";

export function FunktionDialog({
  z,
  gruppe,
  onClose,
}: {
  z: ZugehoerigkeitView | null;
  gruppe: GruppeView;
  onClose: () => void;
}) {
  return (
    <Dialog open={z !== null} onClose={onClose}>
      {z && <FunktionFormular z={z} gruppe={gruppe} />}
    </Dialog>
  );
}

function FunktionFormular({ z, gruppe }: { z: ZugehoerigkeitView; gruppe: GruppeView }) {
  const { personen, zugehoerigkeiten, heute, funktionSetzen } = useGruppenBestand();
  const person = personen[z.contactId];
  const [funktion, setFunktion] = useState(z.function ?? "");
  // Ohne bisherige Funktion gibt es nichts zu beenden — dann ist es ein Eintrag.
  const [modus, setModus] = useState<Modus>(z.function ? "wechsel" : "korrektur");
  const [ab, setAb] = useState(heute);
  const [fehler, setFehler] = useState<string | undefined>();
  const inGruppe = zugehoerigkeiten.filter((x) => x.groupId === gruppe.id).map((x) => x.function);

  async function speichern(): Promise<ActionResult<{ text: string }>> {
    const neu = funktion.trim() || null;
    if (neu === (z.function ?? null)) return { error: "Die Funktion ist unverändert." };
    if (modus === "wechsel") {
      if (!istIsoDatum(ab)) return { error: "Bitte einen gültigen Stichtag.", field: "ab" };
      if (z.since && ab <= z.since) return { error: `Der Stichtag muss nach dem Beginn liegen (${formatDate(z.since)}).`, field: "ab" };
      if (z.until && ab > z.until) return { error: `Der Stichtag liegt nach dem Ende der Zugehörigkeit (${formatDate(z.until)}).`, field: "ab" };
    }
    const r = await funktionSetzen(z.id, neu, modus, ab);
    if (istFehler(r)) return r;
    return {
      success: true,
      text: neu
        ? `${person?.displayName ?? "Person"}: ${neu}${modus === "wechsel" ? ` ab ${formatDate(ab)}` : ""}`
        : `${person?.displayName ?? "Person"}: Funktion entfernt`,
    };
  }

  return (
    <DialogForm
      action={speichern}
      refresh={false}
      success={(r) => ("text" in r ? r.text : null)}
      onError={(m, r) => {
        if (r && "field" in r && r.field === "ab") setFehler(m);
        else toast.error(m);
      }}
    >
      <DialogHeader>
        <DialogTitle>{z.function ? "Funktion ändern" : "Funktion eintragen"}</DialogTitle>
        <DialogDescription>
          {person?.displayName} in „{gruppe.name}“{z.function ? ` — bisher ${z.function}` : ""}
        </DialogDescription>
      </DialogHeader>
      <DialogContent>
        <FunktionsFeld kind={gruppe.kind} gender={person?.gender} wert={funktion} onChange={setFunktion} inGruppe={inGruppe} />

        {z.function && (
          <DialogChoice<Modus>
            label="Wie ändern?"
            value={modus}
            onChange={setModus}
            options={[
              { value: "wechsel", label: "Wechsel zum Stichtag", hint: "Nach einer Wahl oder einem Ämtertausch. Die bisherige Funktion endet am Vortag und bleibt in der Historie." },
              { value: "korrektur", label: "Nur korrigieren", hint: "Der Eintrag war falsch. Er wird überschrieben, ohne Spur in der Historie." },
            ]}
          />
        )}

        {modus === "wechsel" && (
          <Input
            type="date"
            name="ab"
            label="Gilt ab"
            value={ab}
            onChange={(e) => {
              setAb(e.target.value);
              setFehler(undefined);
            }}
            error={fehler}
            hint={istIsoDatum(ab) ? `${z.function ?? "Ohne Funktion"} bis ${formatDate(plusTage(ab, -1))}` : undefined}
          />
        )}
      </DialogContent>
      <DialogFooter>
        <DialogCancel />
        <DialogSubmit>Speichern</DialogSubmit>
      </DialogFooter>
    </DialogForm>
  );
}

export function BeendenDialog({
  z,
  gruppe,
  onClose,
}: {
  z: ZugehoerigkeitView | null;
  gruppe: GruppeView;
  onClose: () => void;
}) {
  return (
    <Dialog open={z !== null} onClose={onClose}>
      {z && <BeendenFormular z={z} gruppe={gruppe} />}
    </Dialog>
  );
}

function BeendenFormular({ z, gruppe }: { z: ZugehoerigkeitView; gruppe: GruppeView }) {
  const { personen, heute, beenden } = useGruppenBestand();
  const person = personen[z.contactId];
  const [bis, setBis] = useState(z.until ?? heute);
  const [fehler, setFehler] = useState<string | undefined>();

  async function speichern(): Promise<ActionResult<{ bis: string }>> {
    if (!istIsoDatum(bis)) return { error: "Bitte ein gültiges Datum.", field: "bis" };
    if (z.since && bis < z.since) return { error: `Das Ende liegt vor dem Beginn (${formatDate(z.since)}).`, field: "bis" };
    const r = await beenden(z.id, bis);
    if (istFehler(r)) return r;
    return { success: true, bis };
  }

  return (
    <DialogForm
      action={speichern}
      refresh={false}
      success={(r) =>
        "bis" in r
          ? {
              title: `${person?.displayName ?? "Zugehörigkeit"} ${r.bis < heute ? "ist nicht mehr" : "bleibt bis " + formatDate(r.bis)} in „${gruppe.name}“`,
              description: "Der Zeitraum bleibt in der Historie der Gruppe.",
            }
          : null
      }
      onError={(m) => setFehler(m)}
    >
      <DialogHeader>
        <DialogTitle>Zugehörigkeit beenden</DialogTitle>
        <DialogDescription>
          {person?.displayName} in „{gruppe.name}“{z.since ? `, dabei seit ${formatDate(z.since)}` : ""}
        </DialogDescription>
      </DialogHeader>
      <DialogContent>
        <Input
          type="date"
          name="until"
          label="Letzter Tag in der Gruppe"
          value={bis}
          onChange={(e) => {
            setBis(e.target.value);
            setFehler(undefined);
          }}
          error={fehler}
          hint={bis > heute ? "Liegt in der Zukunft — bis dahin bleibt die Person aktuell (z. B. Saisonende)." : undefined}
        />
        <DialogNotice icon={<History className="text-text-tertiary h-4 w-4" />}>
          Gelöscht wird nichts: der Zeitraum steht danach unter „Früher“.
          {z.function ? ` Die Funktion ${z.function} endet mit.` : ""} Verteiler und Gruppentermine gelten bis zu diesem Tag.
        </DialogNotice>
      </DialogContent>
      <DialogFooter>
        <DialogCancel />
        <DialogSubmit>Beenden</DialogSubmit>
      </DialogFooter>
    </DialogForm>
  );
}
